/* Main-thread client for the PHP worker — the v1 `PhpRuntime` implementation.
 *
 * Owns one Web Worker per PHP version. The worker is spawned + initialised
 * lazily on first run and reused across runs (the wasm compile is the expensive
 * part). Timeout and cancellation both work by terminating the worker and
 * re-initialising lazily, which is the only reliable way to stop a runaway
 * script executing inside wasm. */

import type { PhpRuntime } from './php-runtime';
import type { RunResult, RunOptions } from './run-result';
import { RunAbortError } from './run-result';
import { runtimeUrl } from './version-registry';
import type { WorkerRequest, WorkerResponse } from '../worker/worker-protocol';

const DEFAULT_TIMEOUT_MS = 10_000;

interface Pending {
  resolve: (r: RunResult) => void;
  reject: (e: unknown) => void;
}

export class PlaygroundRuntime implements PhpRuntime {
  readonly version: string;
  private worker: Worker | null = null;
  private ready: Promise<void> | null = null;
  private readonly pending = new Map<number, Pending>();
  private nextId = 1;

  constructor(version: string) {
    this.version = version;
  }

  /** Spawn + initialise the worker once; subsequent calls await the same boot. */
  private ensureWorker(): Promise<void> {
    if (this.ready) return this.ready;

    const worker = new Worker(new URL('../worker/php-worker.ts', import.meta.url), {
      type: 'module',
      name: `php-${this.version}`,
    });
    this.worker = worker;

    worker.onmessage = (e: MessageEvent<WorkerResponse>) => this.onMessage(e.data);
    // A crashed worker must be discarded (not just have its runs rejected), else
    // the next run() would send into a dead worker and hang until its timeout.
    worker.onerror = (e) => this.reset(new Error(e.message || 'worker crashed'));

    this.ready = new Promise<void>((resolve, reject) => {
      const onReady = (e: MessageEvent<WorkerResponse>) => {
        if (e.data.kind === 'ready') {
          worker.removeEventListener('message', onReady);
          resolve();
        } else if (e.data.kind === 'initError') {
          worker.removeEventListener('message', onReady);
          reject(new Error(e.data.message));
        }
      };
      worker.addEventListener('message', onReady);
      this.send({ kind: 'init', version: this.version, runtimeUrl: runtimeUrl(this.version) });
    });

    // A failed boot must not be cached — allow the next run to retry.
    this.ready.catch(() => this.reset());
    return this.ready;
  }

  private send(msg: WorkerRequest) {
    this.worker?.postMessage(msg);
  }

  private onMessage(msg: WorkerResponse) {
    if (msg.kind === 'result') {
      this.pending.get(msg.id)?.resolve(msg.result);
      this.pending.delete(msg.id);
    } else if (msg.kind === 'runError') {
      this.pending.get(msg.id)?.reject(new Error(msg.message));
      this.pending.delete(msg.id);
    }
  }

  private failAll(err: unknown) {
    for (const p of this.pending.values()) p.reject(err);
    this.pending.clear();
  }

  /** Terminate the worker, drop boot state, and reject every still-pending run.
   * Rejecting ALL pending (not just the run that triggered the reset) prevents
   * orphaned promises hanging forever once Phase 3 runs concurrently. */
  private reset(reason: unknown = new RunAbortError('Cancelled', 'cancel')) {
    this.worker?.terminate();
    this.worker = null;
    this.ready = null;
    this.failAll(reason);
  }

  /** Idle-warm: instantiate the worker (compiles the wasm) ahead of the first
   * run so that run feels instant. Swallows boot failures — warming is a
   * best-effort optimisation, never a hard error (the real run will surface it).
   * Caller must only warm an already-cached version (so this never triggers a
   * silent multi-MB download). */
  async warm(): Promise<void> {
    try {
      await this.ensureWorker();
    } catch {
      /* warming is best-effort; ensureWorker already cleared failed boot state */
    }
  }

  async run(code: string, opts: RunOptions = {}): Promise<RunResult> {
    if (opts.signal?.aborted) throw new RunAbortError('Cancelled', 'cancel');
    await this.ensureWorker();

    const id = this.nextId++;
    const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;

    return new Promise<RunResult>((resolve, reject) => {
      const cleanup = () => {
        clearTimeout(timer);
        opts.signal?.removeEventListener('abort', onAbort);
      };
      const settleResolve = (r: RunResult) => {
        cleanup();
        resolve(r);
      };
      const settleReject = (e: unknown) => {
        cleanup();
        reject(e);
      };
      // Pending entry wraps settle helpers so timer/abort cleanup runs on a
      // normal worker reply too (onMessage resolves/rejects via this entry).
      this.pending.set(id, { resolve: settleResolve, reject: settleReject });

      const timer = setTimeout(() => {
        this.pending.delete(id);
        this.reset(); // kill the runaway script; next run re-inits a fresh worker
        settleReject(new RunAbortError(`Execution timed out (${timeoutMs} ms)`, 'timeout'));
      }, timeoutMs);

      const onAbort = () => {
        this.pending.delete(id);
        this.reset();
        settleReject(new RunAbortError('Cancelled', 'cancel'));
      };
      opts.signal?.addEventListener('abort', onAbort, { once: true });

      this.send({ kind: 'run', id, code, stdin: opts.stdin, args: opts.args });
    });
  }

  dispose() {
    this.reset(new RunAbortError('Cancelled', 'cancel'));
  }
}
